import { z } from 'zod';
import { CUSTOM_SIZE_MIN_CM, CUSTOM_SIZE_MAX_CM } from './sales.js';
// The made-to-order finish axes that a model can flag as PRICED variants (own
// SKU + price) instead of free order-time options. Size is always priced.
export const FINISH_AXES = ['color', 'sideColor', 'fabric', 'density'];
// ── products (variants — each size is one Product) ──────────────────────────
export const productCreateSchema = z.object({
    sku: z.string().min(1).max(40),
    name: z.string().min(1).max(200),
    description: z.string().max(2000).nullable().optional(),
    categoryId: z.string().min(1),
    modelId: z.string().min(1).nullable().optional(),
    size: z.string().max(60).nullable().optional(),
    fabric: z.string().max(60).nullable().optional(),
    color: z.string().max(60).nullable().optional(),
    sideColor: z.string().max(60).nullable().optional(),
    density: z.string().max(60).nullable().optional(),
    barcode: z.string().max(60).nullable().optional(),
    basePrice: z.coerce.number().nonnegative(),
    salePrice: z.coerce.number().nonnegative().nullable().optional(),
    vatRate: z.coerce.number().min(0).max(1).default(0.15),
    // Optional opening stock — initial on-hand balances placed across one or more
    // locations (warehouse and/or shops). Stock-tracked categories only.
    openingStock: z
        .array(z.object({ locationId: z.string().min(1), qty: z.coerce.number().int().min(0) }))
        .optional(),
    attrs: z.record(z.string(), z.unknown()).default({}),
});
export const productUpdateSchema = productCreateSchema.partial().extend({
    isActive: z.boolean().optional(),
});
// Live price preview for a bespoke mattress size — POST /product-models/:id/custom-size/price.
export const customSizePriceSchema = z.object({
    widthCm: z.coerce.number().int().min(CUSTOM_SIZE_MIN_CM).max(CUSTOM_SIZE_MAX_CM),
    lengthCm: z.coerce.number().int().min(CUSTOM_SIZE_MIN_CM).max(CUSTOM_SIZE_MAX_CM),
    fabric: z.string().max(60).nullable().optional(),
    color: z.string().max(60).nullable().optional(),
    sideColor: z.string().max(60).nullable().optional(),
    density: z.string().max(60).nullable().optional(),
});
export const productImageCreateSchema = z.object({
    url: z.string().url(),
    alt: z.string().max(200).optional(),
    isPrimary: z.boolean().default(false),
    displayOrder: z.number().int().min(0).default(0),
});
// Partial update for an existing image — e.g. promote one to primary, or
// tweak its alt text / ordering. All fields optional.
export const productImageUpdateSchema = z
    .object({
    alt: z.string().max(200).nullable().optional(),
    isPrimary: z.boolean().optional(),
    displayOrder: z.number().int().min(0).optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'No fields to update' });
// ── product models (the parent a customer recognises; groups size variants) ──
// A single size variant within a model-create payload.
export const variantInputSchema = z.object({
    size: z.string().max(60).nullable().optional(),
    fabric: z.string().max(60).nullable().optional(),
    color: z.string().max(60).nullable().optional(),
    sideColor: z.string().max(60).nullable().optional(),
    density: z.string().max(60).nullable().optional(),
    sku: z.string().min(1).max(40),
    barcode: z.string().max(60).nullable().optional(),
    basePrice: z.coerce.number().nonnegative(),
    salePrice: z.coerce.number().nonnegative().nullable().optional(),
    vatRate: z.coerce.number().min(0).max(1).default(0.15),
    // Optional opening stock across one or more locations (warehouse and/or shops).
    // Applied only for stock-tracked categories. Empty/omitted = none.
    openingStock: z
        .array(z.object({ locationId: z.string().min(1), qty: z.coerce.number().int().min(0) }))
        .optional(),
});
// A gift bundled with a model. Either a FIXED variant (`productId` — the admin
// commits to one size) OR a whole MODEL (`giftModelId` — the model has several
// sizes and the sales girl picks one at order time). Exactly one must be set.
export const giftLinkInputSchema = z
    .object({
    productId: z.string().min(1).optional(),
    giftModelId: z.string().min(1).optional(),
    qty: z.coerce.number().int().min(1),
})
    .refine((g) => (g.productId == null) !== (g.giftModelId == null), {
    message: 'A gift must reference either a product or a model (exactly one).',
    path: ['productId'],
});
export const productModelCreateSchema = z.object({
    name: z.string().min(1).max(200),
    categoryId: z.string().min(1),
    description: z.string().max(2000).nullable().optional(),
    gift: z.string().max(200).nullable().optional(),
    warranty: z.string().max(200).nullable().optional(),
    attrs: z.record(z.string(), z.unknown()).default({}),
    // Order-time choices (made-to-order, e.g. local mattresses). Empty = none.
    colorOptions: z.array(z.string().min(1).max(40)).default([]),
    sideColorOptions: z.array(z.string().min(1).max(40)).default([]),
    // Made-to-order fabrics — order-time choice like colour (not a stocked SKU).
    fabricOptions: z.array(z.string().min(1).max(40)).default([]),
    // Made-to-order densities — order-time choice like fabric (not a stocked SKU).
    densityOptions: z.array(z.string().min(1).max(40)).default([]),
    // Which finish axes are PRICED variants (own SKU) vs free options above.
    pricedAxes: z.array(z.enum(FINISH_AXES)).default([]),
    // Free gift(s) bundled with the model — a fixed variant or a whole model whose
    // size is chosen at order time, + qty (per unit sold).
    gifts: z.array(giftLinkInputSchema).default([]),
    // Variants may be empty at create time — the multi-step wizard adds them in a
    // later step via POST /product-models/:id/variants. A model with no priced
    // variant is auto-hidden from the catalogue until it has one.
    variants: z.array(variantInputSchema).default([]),
});
// Step 2 of the wizard: add a batch of variants to an existing model.
export const productModelAddVariantsSchema = z.object({
    variants: z.array(variantInputSchema).min(1, 'At least one variant is required'),
});
// Step 3 of the wizard: set opening stock per variant per location (GL-neutral).
export const openingStockSchema = z.object({
    levels: z.array(z.object({
        productId: z.string().min(1),
        locationId: z.string().min(1),
        qty: z.coerce.number().int().min(0),
    })),
});
export const productModelUpdateSchema = z.object({
    name: z.string().min(1).max(200).optional(),
    categoryId: z.string().min(1).optional(),
    description: z.string().max(2000).nullable().optional(),
    gift: z.string().max(200).nullable().optional(),
    warranty: z.string().max(200).nullable().optional(),
    attrs: z.record(z.string(), z.unknown()).optional(),
    colorOptions: z.array(z.string().min(1).max(40)).optional(),
    sideColorOptions: z.array(z.string().min(1).max(40)).optional(),
    fabricOptions: z.array(z.string().min(1).max(40)).optional(),
    densityOptions: z.array(z.string().min(1).max(40)).optional(),
    pricedAxes: z.array(z.enum(FINISH_AXES)).optional(),
    gifts: z.array(giftLinkInputSchema).optional(),
    isActive: z.boolean().optional(),
    displayOrder: z.number().int().min(0).optional(),
});
// ── stock ────────────────────────────────────────────────────────────────────
export const stockReceiveSchema = z.object({
    productId: z.string().min(1),
    locationId: z.string().min(1),
    qty: z.number().int().positive(),
    note: z.string().max(500).optional(),
});
export const stockAdjustSchema = z.object({
    productId: z.string().min(1),
    locationId: z.string().min(1),
    delta: z.number().int(), // positive or negative
    reason: z.string().min(1).max(500), // mandatory for adjustments
});
export const stockThresholdSchema = z.object({
    minThreshold: z.number().int().min(0),
});
// ── display units (showroom floor pieces) ────────────────────────────────────
export const displayUnitAssignSchema = z.object({
    productId: z.string().min(1),
    locationId: z.string().min(1), // must be a shop-kind location
    // true → the piece comes out of the shop's sellable stock (available must
    // cover it); false → floor onboarding of a piece the system never held.
    fromStock: z.boolean(),
    condition: z.string().max(500).optional().nullable(),
    note: z.string().max(500).optional().nullable(),
});
export const displayUnitWriteOffSchema = z.object({
    reason: z.string().min(1).max(500), // mandatory, like stock adjustments
});
export const displayUnitFlagForSaleSchema = z.object({
    // VAT-inclusive gross, like every stored price.
    clearancePrice: z.coerce.number().positive(),
});
export const displayUnitUpdateSchema = z.object({
    condition: z.string().max(500).optional().nullable(),
    note: z.string().max(500).optional().nullable(),
});
// ── numbered stock units (imported mattresses) ───────────────────────────────
// One row per physical piece, carrying the number written on it by hand, so a
// delivery records WHICH unit went to the customer. Tracking layer only —
// StockLevel stays authoritative for availability. See stock-units.service.ts.
/** Free-text as written on the mattress; not forced to any format. */
export const unitNumberSchema = z.string().trim().min(1).max(60);
export const stockUnitsAddSchema = z.object({
    productId: z.string().min(1),
    locationId: z.string().min(1),
    // The operator usually pastes or types a short list — five imported mattresses
    // already numbered on the rack. Capped so a bad paste can't create thousands.
    unitNumbers: z.array(unitNumberSchema).min(1).max(200),
    note: z.string().max(500).optional().nullable(),
});
export const stockUnitUpdateSchema = z.object({
    unitNumber: unitNumberSchema.optional(),
    // Moving a unit between locations. Quantity is moved by the normal transfer
    // flow; this just keeps the number's whereabouts honest.
    locationId: z.string().min(1).optional(),
    note: z.string().max(500).optional().nullable(),
});
export const stockUnitWriteOffSchema = z.object({
    reason: z.string().min(1).max(500), // mandatory, like stock adjustments
});
/** Which numbered pieces are being loaded for a delivery-note line. */
export const deliveryLineUnitsSchema = z.object({
    stockUnitIds: z.array(z.string().min(1)).max(50),
});
// ── transfers ────────────────────────────────────────────────────────────────
export const stockTransferCreateSchema = z.object({
    productId: z.string().min(1),
    fromLocationId: z.string().min(1),
    toLocationId: z.string().min(1),
    qty: z.number().int().positive(),
    note: z.string().max(500).optional(),
});
// Body for POST /stock-transfers/:id/receive. receivedQty is optional —
// when omitted the receiving side acknowledges the full expected qty.
// When provided + less than expected, the shortage is logged as an in-transit
// loss (sales girl should explain in note).
export const stockTransferReceiveSchema = z.object({
    receivedQty: z.coerce.number().int().min(0).optional(),
    note: z.string().max(500).optional().nullable(),
});
//# sourceMappingURL=catalogue.js.map